/**
 * `window.__marg.render` namespace (art §12 names; architecture §8.1). Built from an `EngineRenderer`; F1's debug API
 * mounts it (`debug` feature → `render` namespace), the art lab mounts it directly when it runs standalone.
 */
import type { CreatureHandle, QualityTier, Vec3 } from '../shared';
import { paletteTable } from '../content/palette';
import { LADDER, type Severity } from '../content/fx/ladder';
import { hexToLinear, TOKENS } from '../content/palette';
import type { EngineRenderer } from './renderer';

export interface RenderDebugNamespace {
  getQuality(): QualityTier;
  setQuality(t: QualityTier): Promise<void>;
  caps(): { triptych: boolean; faceCam: boolean; fxOverlay: boolean; msaa: number; smear: boolean };
  setDebugView(name: string | null): void;
  stats(): Record<string, unknown>;
  fx(name: string, opts?: Record<string, unknown>): void;
  freeze(t: number): void;
  unfreeze(): void;
  samplePixel(x: number, y: number): Promise<[number, number, number]>;
  capture(): Promise<string>;
  debugBounds(): unknown;
  debugLift(slot: number, h: number): void;
  setHpPct(slot: number, p: number): void;
  palette(): unknown;
  errors(): string[];
  loseDevice(): Promise<void>;
}

export function createRenderDebug(r: EngineRenderer, opts: { creatures?: () => CreatureHandle[]; contactPoint?: () => Vec3 } = {}): RenderDebugNamespace {
  let seed = 1;
  const handles = (): CreatureHandle[] => (opts.creatures ? opts.creatures() : []);
  return {
    getQuality: () => r.quality,
    setQuality: (t) => r.setQuality(t),
    caps: () => ({ ...r.caps(), faceCam: false, fxOverlay: false }),
    setDebugView: (name) => r.debug.setView(name),
    stats: () => {
      const ft = r.frameTimes();
      return {
        ...r.stats, ...r.extra, tier: r.quality, p50Ms: ft.p50, p90Ms: ft.p90,
        frameBreakPixels: r.stats.frameBreakCount > 0 ? 1 : 0, frameBreakMax1s: r.stats.frameBreakCount,
        lastHitStopMs: 0, liveBands: 0, faceCam: { active: false }, artTexMB: 0,
      };
    },
    fx: (name, o = {}) => {
      const target = handles()[Number(o.target ?? 1)] ?? null;
      const at: Vec3 = (o.at as Vec3 | undefined) ?? (opts.contactPoint ? opts.contactPoint() : target ? [...target.geom.parts.chest] as Vec3 : [0, 0.6, 0]);
      const sev = (o.severity as Severity | undefined) ?? (name === 'ko' ? 'ko' : 'heavy');
      const row = LADDER[sev];
      if (name === 'hit' || name === 'ko') {
        const el = row.element ?? 'ink';
        r.vfx.burst('splat', at, { seed: seed++, dir: [1, 0.35, 0], count: row.particles.splat, element: el });
        if (row.particles.tuft) r.vfx.burst('tuft', at, { seed: seed++, dir: [1, 0.6, 0], count: row.particles.tuft });
        if (row.particles.sparkle) r.vfx.burst('sparkle', at, { seed: seed++, count: row.particles.sparkle });
        if (row.particles.rubric) r.vfx.burst('rubric', at, { seed: seed++, count: 1 });
        if (row.flash && target) {
          const c = row.flash.color === 'vermilion' ? hexToLinear(TOKENS.vermilion) : hexToLinear(TOKENS.leadWhite);
          target.setFx({ hitFlash: row.flash.amount, fxColor: [c[0], c[1], c[2], 1] });
          const ms = row.flash.ms;
          setTimeout(() => target.setFx({ hitFlash: 0 }), ms);
        }
        r.panel.kick(row.shakePx);
        if (row.punchPct) r.camera.punch(row.punchPct);
        if (row.impactFrame) r.panel.impact(sev === 'ko' ? 'ko' : 'crit');
        if (name === 'ko' && target) target.setFx({ frameBreak: 1 });
      } else if (name === 'impact') {
        r.panel.impact(sev === 'ko' ? 'ko' : 'crit');
        r.panel.kick(LADDER[sev].shakePx);
      } else if (name === 'sparkle') {
        r.vfx.burst('sparkle', at, { seed: seed++, count: Number(o.count ?? 10) });
      }
    },
    freeze: (t) => r.freeze(t),
    unfreeze: () => r.freeze(null),
    samplePixel: (x, y) => r.debug.samplePixel(x, y),
    capture: () => r.debug.capture(),
    debugBounds: () => r.debug.bounds(),
    debugLift: (slot, h) => r.debug.lift(slot, h),
    setHpPct: (slot, p) => {
      const h = handles()[slot];
      if (h) h.setFx({ wearLevel: p <= 0.25 ? 3 : p <= 0.5 ? 2 : p <= 0.75 ? 1 : 0 });
      if (slot === 0) r.panel.setBurn(p < 0.25 ? 0.5 : 0);
    },
    palette: () => paletteTable(),
    errors: () => r.errors.slice(),
    loseDevice: () => r.simulateDeviceLoss(),
  };
}
